import { Box, Stack, Tooltip } from '@mui/material';
import { FormattedMessage, FormattedNumber, FormattedTime, useIntl } from 'react-intl';
import { ImportStatus } from '@synergy/models';
import { PaloColumn, PaloListView, PaloRowAction } from '@synergy/components-palo';
import { PoolBadge, PoolButton, PoolModal, PoolTypography, VerticalDivider } from '@synergy/components';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ImportJob } from '../../models/import-job';
import { ImportStatusChip } from '../../components/import-status-chip';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import { abortImport, deleteImport, fetchImports } from '../../store/all/import-actions';
import { isImportDeletable } from '../../utils/is-import-deletable';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export const ImportOverviewAll = () => {
    const intl = useIntl();

    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useThunkDispatch();
    const [importToDelete, setImportToDelete] = useState<ImportJob | null>(null);
    const [importToAbort, setImportToAbort] = useState<ImportJob | null>(null);
    const { requesting, importJobs: paginatedImports } = useAppSelector(state => state.import);

    // The list sorts on the server, so the chosen column travels in the address.
    // Until one is chosen the list claims no order of its own — an arrow over a
    // column the server is not sorting by says something untrue.
    const sortColumn = searchParams.get('sort');
    const sort = sortColumn
        ? { key: sortColumn, order: ('desc' === searchParams.get('order') ? 'desc' : 'asc') as 'asc' | 'desc' }
        : undefined;

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    /** How many articles the run holds, and how they came out. */
    const counts = (job: ImportJob) => (
        <Stack flexDirection="row" justifyContent="flex-start" gap={1}>
            <Tooltip title={<FormattedMessage id="app.toolTip.totalArticles" />} placement="top">
                <Box>
                    <PoolBadge content={<FormattedNumber value={job.itemCount} />} color="info" />
                </Box>
            </Tooltip>
            <VerticalDivider />
            <Tooltip title={<FormattedMessage id="app.toolTip.publishedArticles" />} placement="top">
                <Box>
                    <PoolBadge content={<FormattedNumber value={job.publishedItemCount} />} color="success" />
                </Box>
            </Tooltip>
            <Tooltip title={<FormattedMessage id="app.toolTip.failedArticles" />} placement="top">
                <Box>
                    <PoolBadge content={<FormattedNumber value={job.failedItemCount} />} color="error" />
                </Box>
            </Tooltip>
            <Tooltip title={<FormattedMessage id="app.toolTip.warningArticles" />} placement="top">
                <Box>
                    <PoolBadge content={<FormattedNumber value={job.warningItemCount} />} color="warning" />
                </Box>
            </Tooltip>
        </Stack>
    );

    const columns: PaloColumn<ImportJob>[] = [
        {
            key: 'number',
            type: 'documentNumber',
            sortKey: 'number',
            head: <FormattedMessage id="app.table.column.number" />,
            cell: job => job.number,
        },
        {
            key: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: job => (
                <ImportStatusChip
                    importJob={job}
                    publishDate={
                        job.publishDate
                            ? intl.formatDate(job.publishDate, { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : undefined
                    }
                />
            ),
        },
        {
            key: 'name',
            type: 'name',
            sortKey: 'name',
            head: <FormattedMessage id="app.table.column.title" />,
            cell: job => job.name,
        },
        {
            key: 'supplier',
            type: 'name',
            head: <FormattedMessage id="app.table.column.supplier" />,
            cell: job => job.supplierAccount.name,
        },
        {
            key: 'counter',
            // Four counts side by side, up to five digits each: a single count's
            // 80 pixels cut them to "4.4" and "10.7".
            width: '250px',
            align: 'left',
            head: <FormattedMessage id="app.table.column.counter" />,
            cell: counts,
        },
        {
            key: 'assignedUser',
            type: 'name',
            head: <FormattedMessage id="app.common.responsibleContact" />,
            cell: job => job.responsibleContact?.fullName,
        },
        {
            key: 'createdAt',
            // Date and time, which a date's 120 pixels cut short.
            type: 'timestamp',
            sortKey: 'createdAt',
            head: <FormattedMessage id="app.table.column.createdAt" />,
            cell: job => <FormattedTime value={job.createdAt} year="numeric" month="short" day="2-digit" />,
        },
    ];

    useEffect(() => {
        dispatch(fetchImports(searchParams));
    }, [dispatch, searchParams]);

    const handleDelete = () => {
        if (importToDelete != null) {
            dispatch(deleteImport(importToDelete.id));
        }
        setImportToDelete(null);
    };

    const handleAbort = () => {
        if (importToAbort != null) {
            dispatch(abortImport(importToAbort.id));
        }
        setImportToAbort(null);
    };

    // What a run allows depends on where it is: a finished one can be removed,
    // a running one can be stopped, and one that is neither offers nothing.
    const rowActions = (job: ImportJob): PaloRowAction[] => [
        ...(isImportDeletable(job.status)
            ? [
                  {
                      label: (
                          <FormattedMessage
                              id="app.menu.action.delete"
                              values={{ value: <FormattedMessage id="app.common.import" /> }}
                          />
                      ),
                      onClick: () => setImportToDelete(job),
                  },
              ]
            : []),
        ...(ImportStatus.Publishing === job.status
            ? [{ label: <FormattedMessage id="app.imports.abort" />, onClick: () => setImportToAbort(job) }]
            : []),
    ];

    const deleteActionChildren = [
        <PoolButton key="1" styleType="firstLevel" variant="text" onClick={() => setImportToDelete(null)}>
            <FormattedMessage id="app.common.cancel" />
        </PoolButton>,
        <PoolButton key="2" styleType="firstLevel" color="secondary" onClick={handleDelete}>
            <FormattedMessage id="app.common.delete" />
        </PoolButton>,
    ];

    const abortActionChildren = [
        <PoolButton key="1" styleType="firstLevel" variant="text" onClick={() => setImportToAbort(null)}>
            <FormattedMessage id="app.imports.abortDialog.cancel" />
        </PoolButton>,
        <PoolButton key="2" styleType="firstLevel" color="secondary" onClick={handleAbort}>
            <FormattedMessage id="app.imports.abortDialog.confirm" />
        </PoolButton>,
    ];

    return (
        <>
            <PaloListView<ImportJob, number>
                embedded={true}
                columns={columns}
                items={paginatedImports.items}
                getRowId={job => job.id}
                requesting={requesting}
                emptyLabel="Keine Importe."
                rowHref={job => ROUTES.products.import.detail.replace(':id', job.id.toString())}
                rowActions={rowActions}
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                pagination={{
                    count: paginatedImports.total,
                    page: Number(searchParams.get('page') ?? 1),
                    limit: Number(searchParams.get('limit') ?? 50),
                    onPageChange: page => updateParams({ page: String(page) }),
                    onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
                }}
            />

            <PoolModal
                isVisible={!!importToAbort}
                title={<FormattedMessage id="app.imports.abort" />}
                actionChildren={abortActionChildren}
                handleClose={() => setImportToAbort(null)}
                hasModalTypography={true}
            >
                <PoolTypography id="app.imports.abort.content" />
            </PoolModal>

            <PoolModal
                isVisible={!!importToDelete}
                title={<FormattedMessage id="app.imports.delete.title" />}
                actionChildren={deleteActionChildren}
                handleClose={() => setImportToDelete(null)}
                hasModalTypography={true}
            >
                <PoolTypography id="app.imports.delete.content" values={{ name: importToDelete?.name }} />
            </PoolModal>
        </>
    );
};
